import type { Environment } from '@zelisight/contract';

import type { ResolvedRange } from '@/lib/range';

/**
 * TanStack Query cache keys.
 *
 * The single rule this file exists to enforce: **every project scoped key
 * begins `['project', projectId]`**. Two projects can never collide in the
 * cache, and `queryClient.removeQueries({ queryKey: qk.project(id) })` evicts
 * one project's data without touching another's. Switching project therefore
 * cannot show the previous project's numbers even for one frame.
 *
 * Range, environment and granularity are part of every key that depends on
 * them, per plan 5.10, so changing any of them refetches everything coherently.
 */

/** The serialisable slice of a range that a cache key depends on. */
function rangeKey(range: ResolvedRange): {
  from: string;
  to: string;
  granularity: string;
} {
  return { from: range.from, to: range.to, granularity: range.granularity };
}

export const qk = {
  /** Identity, orgs and projects. Not project scoped. */
  me: () => ['me'] as const,

  /** Root of everything belonging to one project. Use for targeted eviction. */
  project: (projectId: string) => ['project', projectId] as const,

  overview: (
    projectId: string,
    env: Environment,
    range: ResolvedRange,
    exactUniques: boolean,
  ) => ['project', projectId, 'overview', env, rangeKey(range), { exactUniques }] as const,

  sessionMetrics: (projectId: string, env: Environment, range: ResolvedRange) =>
    ['project', projectId, 'session-metrics', env, rangeKey(range)] as const,

  /**
   * Campaign and channel attribution. The row cap is in the key because it
   * changes the answer: a cached top 20 must not be served to a request that
   * asked for the top 50.
   */
  attribution: (
    projectId: string,
    env: Environment,
    range: ResolvedRange,
    limit: number,
  ) => ['project', projectId, 'attribution', env, rangeKey(range), { limit }] as const,

  /** Audience breakdowns. The row cap is in the key for the same reason. */
  audience: (
    projectId: string,
    env: Environment,
    range: ResolvedRange,
    limit: number,
  ) => ['project', projectId, 'audience', env, rangeKey(range), { limit }] as const,

  heatmapPages: (projectId: string, env: Environment, range: ResolvedRange) =>
    ['project', projectId, 'heatmap-pages', env, rangeKey(range)] as const,

  pageHeatmap: (
    projectId: string,
    env: Environment,
    range: ResolvedRange,
    path: string,
    device: string,
  ) => ['project', projectId, 'page-heatmap', env, rangeKey(range), { path, device }] as const,

  heatmapSnapshot: (projectId: string, env: Environment, path: string, device: string) =>
    ['project', projectId, 'heatmap-snapshot', env, { path, device }] as const,

  /**
   * One funnel job, keyed by the job id the server minted.
   *
   * Keyed on the job rather than on the funnel definition on purpose. The
   * definition already has a server side cache (15 minutes, keyed by a hash of
   * the request), so caching it again here would mean two caches with different
   * lifetimes disagreeing about the same funnel.
   */
  funnelJob: (projectId: string, jobId: string) =>
    ['project', projectId, 'funnel-job', jobId] as const,

  sessions: (projectId: string, env: Environment, range: ResolvedRange, limit: number) =>
    ['project', projectId, 'sessions', env, rangeKey(range), { limit }] as const,

  /**
   * The live feed. The cursor is deliberately NOT in the key: the poll advances
   * a cursor held outside the cache and appends to one accumulating entry, so a
   * new cursor must not mint a new cache line every five seconds.
   */
  live: (projectId: string, env: Environment) =>
    ['project', projectId, 'live', env] as const,

  eventDetail: (
    projectId: string,
    env: Environment,
    range: ResolvedRange,
    eventName: string,
    breakdown: readonly string[],
  ) =>
    [
      'project',
      projectId,
      'event',
      eventName,
      env,
      rangeKey(range),
      // Sorted so that picking `plan, seats` and `seats, plan` hit one cache
      // entry rather than two identical ones.
      [...breakdown].sort(),
    ] as const,

  /** The breakdown offer list. Range scoped, because "observed" means in range. */
  eventProperties: (
    projectId: string,
    env: Environment,
    range: ResolvedRange,
    eventName: string,
  ) => ['project', projectId, 'event', eventName, 'properties', env, rangeKey(range)] as const,

  // `filter` is part of the key, not a detail. Identified and anonymous are two
  // different index reads that both answer "people", and caching them under one
  // key would show the previous half while the other loaded.
  people: (
    projectId: string,
    env: Environment,
    query: string,
    filter: string,
    cursor: string | null,
  ) => ['project', projectId, 'people', env, query, filter, cursor] as const,

  eventNames: (projectId: string, env: Environment, range: ResolvedRange) =>
    ['project', projectId, 'event-names', env, range.from, range.to] as const,

  // The window start is part of the key, rounded to the hour. Without it a
  // rolling window would serve yesterday's MAU from cache all day.
  activity: (projectId: string, env: Environment, windowStart: string) =>
    ['project', projectId, 'activity', env, windowStart] as const,

  person: (projectId: string, distinctId: string, env: Environment) =>
    ['project', projectId, 'person', distinctId, env] as const,

  personTimeline: (
    projectId: string,
    distinctId: string,
    env: Environment,
    range: ResolvedRange,
    cursor: string | null,
  ) =>
    [
      'project',
      projectId,
      'person',
      distinctId,
      'timeline',
      env,
      rangeKey(range),
      cursor,
    ] as const,

  /** One page of the sessions list. The cursor is the page, so it is the key. */
  sessionPage: (projectId: string, env: Environment, range: ResolvedRange, cursor: string | null) =>
    ['project', projectId, 'session-page', env, rangeKey(range), { cursor }] as const,

  personSessions: (
    projectId: string,
    distinctId: string,
    env: Environment,
    range: ResolvedRange,
  ) =>
    [
      'project',
      projectId,
      'person',
      distinctId,
      'sessions',
      env,
      rangeKey(range),
    ] as const,

  /** One session's replay manifest. Signed URLs, so never prefetched. */
  sessionReplay: (projectId: string, sessionId: string) =>
    ['project', projectId, 'session', sessionId, 'replay'] as const,

  onboarding: (projectId: string) => ['project', projectId, 'onboarding'] as const,

  keys: (projectId: string) => ['project', projectId, 'keys'] as const,

  settings: (projectId: string) => ['project', projectId, 'settings'] as const,
} as const;
